import React, { useEffect, useState } from "react";
import { Sparkles, Flame, TrendingUp, X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { getStoredUser } from "./RegisterModal";
import { getWelcomeBack } from "../lib/api";
import { useI18n } from "../i18n";

const SESSION_FLAG = "goldora_welcome_back_shown";

// Re-engagement modal: shown ONCE per browser session when user returns after 7+ days.
export default function WelcomeBackModal() {
  const { lang } = useI18n();
  const pt = lang === "pt";
  const [data, setData] = useState(null);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    const user = getStoredUser();
    if (!user?.email) return;
    if (sessionStorage.getItem(SESSION_FLAG) === "1") return;

    let alive = true;
    getWelcomeBack(user.email, lang)
      .then((d) => {
        if (!alive) return;
        if (d?.show) {
          setData(d);
          sessionStorage.setItem(SESSION_FLAG, "1");
        }
      })
      .catch(() => {/* silent */});
    return () => { alive = false; };
  }, [lang]);

  if (!data || closed) return null;

  const first = (data.name || "").trim().split(" ")[0];
  const greeting = first
    ? (pt ? `Bem-vindo de volta, ${first}.` : `Welcome back, ${first}.`)
    : (pt ? "Bem-vindo de volta." : "Welcome back.");

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-md grid place-items-center p-4 animate-in fade-in"
      data-testid="welcome-back-modal"
      onClick={() => setClosed(true)}
    >
      <div
        className="relative w-full max-w-lg rounded-2xl border border-gold-500/30 bg-gradient-to-br from-gold-500/[0.06] via-black to-black overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.8)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          aria-hidden
          className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-25 blur-3xl"
          style={{ background: "radial-gradient(circle, #E9B934 0%, transparent 60%)" }}
        />
        <button
          onClick={() => setClosed(true)}
          aria-label="Close"
          data-testid="welcome-back-close"
          className="absolute top-4 right-4 text-neutral-500 hover:text-white z-10"
        >
          <X size={18} />
        </button>

        <div className="relative p-7 sm:p-9">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold-500/40 bg-gold-500/10 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-gold-300 font-mono">
            <Sparkles size={11} />
            {pt ? "Sentimos sua falta" : "We missed you"}
          </div>

          <h2 className="mt-5 font-display text-3xl sm:text-4xl tracking-tight text-white leading-tight">
            {greeting}
          </h2>
          <p className="mt-2 text-sm text-neutral-400">
            {pt
              ? `Você esteve fora por ${data.days_away} ${data.days_away === 1 ? "dia" : "dias"}. Aqui está o que rolou:`
              : `You've been away for ${data.days_away} ${data.days_away === 1 ? "day" : "days"}. Here's what happened:`}
          </p>

          <div className="mt-6 space-y-3" data-testid="welcome-back-cards">
            {data.current_streak > 0 && (
              <Card
                icon={Flame}
                tone="orange"
                eyebrow={pt ? "Sua sequência" : "Your streak"}
                title={`${data.current_streak} ${data.current_streak === 1 ? (pt ? "dia" : "day") : (pt ? "dias" : "days")}`}
                desc={pt
                  ? "Mantenha o ritmo — faça o check-in de hoje."
                  : "Keep the momentum — check in today."}
                cta={pt ? "Check-in" : "Check in"}
                to="/coach"
                onClose={() => setClosed(true)}
              />
            )}

            {data.market_summary && (
              <Card
                icon={TrendingUp}
                tone="emerald"
                eyebrow={pt ? "Mercado nas últimas 24h" : "Last 24h market"}
                title={data.market_summary}
                desc={pt ? "Veja todas as classes ao vivo." : "See every asset live."}
                cta={pt ? "Ver mercado" : "View market"}
                to="/mercado"
                onClose={() => setClosed(true)}
              />
            )}

            {data.weekly_headline && (
              <Card
                icon={Sparkles}
                tone="gold"
                eyebrow={pt ? "Decisão da Semana" : "Decision of the Week"}
                title={data.weekly_pair || data.weekly_headline.slice(0, 60)}
                desc={data.weekly_headline.slice(0, 110)}
                cta={pt ? "Ler análise" : "Read analysis"}
                to="/"
                onClose={() => setClosed(true)}
              />
            )}
          </div>

          <button
            onClick={() => setClosed(true)}
            data-testid="welcome-back-dismiss"
            className="mt-6 w-full text-center text-xs font-mono uppercase tracking-[0.22em] text-neutral-500 hover:text-neutral-300 py-2"
          >
            {pt ? "Continuar" : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}

const TONE = {
  orange:  { bg: "bg-orange-500/10",  border: "border-orange-500/30",  fg: "text-orange-300" },
  emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", fg: "text-emerald-300" },
  gold:    { bg: "bg-gold-500/10",    border: "border-gold-500/40",    fg: "text-gold-300" },
};

function Card({ icon: Icon, tone, eyebrow, title, desc, cta, to, onClose }) {
  const t = TONE[tone] || TONE.gold;
  return (
    <Link
      to={to}
      onClick={onClose}
      className={`flex items-start gap-3 p-4 rounded-xl border ${t.border} ${t.bg} hover:bg-white/[0.04] transition-colors group`}
      data-testid={`welcome-back-card-${tone}`}
    >
      <div className={`w-9 h-9 rounded-md grid place-items-center shrink-0 ${t.bg} border ${t.border} ${t.fg}`}>
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-neutral-500">{eyebrow}</div>
        <div className="mt-0.5 font-display text-base text-white leading-tight truncate">{title}</div>
        <div className="mt-0.5 text-xs text-neutral-400 leading-snug">{desc}</div>
      </div>
      <span className={`shrink-0 self-center text-xs ${t.fg} font-mono inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform`}>
        {cta} <ArrowRight size={11} />
      </span>
    </Link>
  );
}
